import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Shield, Users, Car, BarChart2, AlertOctagon, LogOut, Menu, X, Settings, MessageSquare, CreditCard, Sparkles, Clock, FileSearch, Bot } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { callRpc } from '../../lib/rpcClient';

export function AdminLayout() {
  const { user, loading, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [badges, setBadges] = useState<{ unresolved_errors: number; new_support: number }>({
    unresolved_errors: 0,
    new_support: 0
  });

  useEffect(() => {
    if (user && isAdmin) {
      const fetchBadges = async () => {
        try {
          const counts = await callRpc('get_admin_badge_counts');
          setBadges(counts);
        } catch (e) {
          console.error('Failed to fetch admin badges:', e);
        }
      };
      
      fetchBadges();
      const interval = setInterval(fetchBadges, 300000); // Refresh every 5 mins
      return () => clearInterval(interval);
    }
  }, [user, isAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col md:flex-row items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 font-primary text-white">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-20 h-20 bg-red-500/10 border border-red-500/20 rounded-3xl flex items-center justify-center mx-auto mb-4">
            <Shield size={40} className="text-red-500" />
          </div>
          <h1 className="text-2xl font-black">Restricted Access</h1>
          <p className="text-slate-500">You do not have administrative privileges to access this area.</p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-white/5 border border-white/10 py-3 rounded-2xl font-bold hover:bg-white/10 transition-all"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    signOut();
    navigate('/');
  };

  const navLinks = [
    { to: '/admin', icon: Shield, label: 'Dashboard', end: true },
    { to: '/admin/users', icon: Users, label: 'Users' },
    { to: '/admin/cars', icon: Car, label: 'Cars' },
    { to: '/admin/analytics', icon: BarChart2, label: 'Analytics' },
    { to: '/admin/pricing', icon: CreditCard, label: 'Pricing' },
    { to: '/admin/car-tasks', icon: Sparkles, label: 'Car Tasks' },
    { to: '/admin/support', icon: MessageSquare, label: 'Support', badge: badges.new_support },
    { to: '/admin/login-times', icon: Clock, label: 'Login Times' },
    { to: '/admin/errors', icon: AlertOctagon, label: 'Errors', badge: badges.unresolved_errors },
    { to: '/admin/tests', icon: Sparkles, label: 'System Tests' },
    { to: '/admin/jarvis-tests', icon: Bot, label: 'Jarvis Tests' },
    { to: '/admin/audits', icon: FileSearch, label: 'Audits' },
    { to: '/admin/types', icon: Settings, label: 'Expense Types' },
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-slate-900 border-r border-slate-800 text-slate-300 font-primary">
      <div className="p-6 flex flex-col items-center border-b border-slate-800">
        <div className="p-3 bg-blue-500/20 rounded-2xl border border-blue-500/30 mb-3">
          <Shield size={28} className="text-blue-400" />
        </div>
        <h2 className="text-xl font-black text-white px-2 text-center tracking-tight">Admin Console</h2>
      </div>

      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        {navLinks.map(({ to, icon: Icon, label, end, badge }: any) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setIsMobileMenuOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl font-bold transition-all ${
                isActive 
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' 
                  : 'hover:bg-slate-800 hover:text-white'
              }`
            }
          >
            <Icon size={20} />
            <span className="flex-1">{label}</span>
            {badge !== undefined && badge > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-black pointer-events-none ${
                label === 'Errors' ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
              }`}>
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-800">
        <button
          onClick={handleLogout}
          className="flex items-center justify-center gap-2 w-full p-3 bg-red-500/10 text-red-400 rounded-xl font-bold hover:bg-red-500/20 transition-all border border-red-500/20"
        >
          <LogOut size={20} />
          <span>Logout</span>
        </button>
        <button
          onClick={() => navigate('/')}
          className="w-full mt-2 text-slate-500 text-sm font-semibold hover:text-slate-300 py-2"
        >
          Back to user app
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col md:flex-row">
      {/* Mobile Top Header */}
      <div className="md:hidden flex items-center justify-between p-4 bg-slate-900 border-b border-slate-800 text-white">
        <div className="flex items-center gap-2">
          <Shield size={24} className="text-blue-400" />
          <span className="font-black">Admin Console</span>
        </div>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2">
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 top-16 bg-slate-950">
          <SidebarContent />
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden md:block w-64 flex-shrink-0 h-screen sticky top-0">
        <SidebarContent />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 text-slate-200 overflow-x-hidden p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
